import 'react-native-gesture-handler';
import { supabase, queryClient, asyncStoragePersister } from '@/data/client'
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AppState, Text, View, Image, LogBox, TouchableOpacity, StatusBar, ActivityIndicator, Appearance, Platform, useWindowDimensions } from 'react-native';
import * as SplashScreen from 'expo-splash-screen';
import * as Application from 'expo-application';
import QueryPersistenceProvider from '@/components/General/QueryPersistenceProvider'
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import * as LocalAuthentication from 'expo-local-authentication';
import { useFonts } from 'expo-font';
import * as Sentry from '@sentry/react-native';
import useAuthStore from '@/data/store/useAuthStore';
import { lightTheme, darkTheme } from '@/utils/constants/themes';
import * as Notifications from 'expo-notifications';
import useLoader, {FullScreenLoader} from '@/utils/hooks/useLoader';
import { getCachedOrgID, getOrganizationEntitlements, getUsersOrganizations, logOutOfOrganization } from '@/data/organization';
import { readPlanMarker } from '@/utils/organizationEntitlements';
import { getLoggedInUserInfo } from '@/data/people';
import { getDeviceByID } from '@/data/settings';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { cleanupLocalAuthState, flushSentryWithTimeout, isSignOutInProgress, reportPendingSignOutContext, signOut } from '@/utils/signOut';
import { ensureFreshSession, restoreSessionWithRetry } from '@/utils/authRefresh';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { shouldPersistQuery } from '@/utils/react-query/persistence';
import { configureReanimatedLogger, ReanimatedLogLevel } from 'react-native-reanimated';
import DrawerMenu from '@/components/General/DrawerMenu';
import WebHeader from '@/components/General/WebHeader';
import usePlatform from '@/utils/hooks/usePlatform';
import useStartupChecks from '@/utils/hooks/useStartupChecks';
import useStartupUpdates from '@/utils/hooks/useStartupUpdates';
import useNotificationObserver from '@/utils/hooks/useNotificationObserver';
import { ConfirmationModal } from '@/utils/showAlert';
import useKioskStore from '@/data/store/useKioskStore';
import usePinnedPostsStore from '@/data/store/usePinnedPostsStore';
import useBoardsFeedStore from '@/data/store/useBoardsFeedStore';
import checkForAppUpdates from '@/stupid-workarounds/checkForAppUpdates'
import TooltipContainer from '@/components/General/Tooltip';
import GuideProvider from '@/components/Guides/GuideProvider';
import ErrorScreen from '@/components/General/ErrorScreen';
import MaintenanceScreen from '@/components/General/MaintenanceScreen';
import UpdateRequiredScreen from '@/components/General/UpdateRequiredScreen';
import SmartAppBanner from '@/components/General/AndroidAppBanner';
import { mobileNotificationHandler, desktopNotificationHandler } from '@/utils/notificationHandler';
import useNotificationBadges from '@/utils/hooks/useNotificationBadges';
import { Slot, ThemeProvider, usePathname, useRouter, useSegments } from 'expo-router';
import { StyleSheet, UnistylesRuntime } from 'react-native-unistyles'
import ReachToast from '@/components/General/ReachToast';
import AchievementHost from '@/components/General/AchievementHost';
import MilestoneCelebrationProvider from '@/components/Milestones/MilestoneCelebrationProvider';
import NetInfo from '@react-native-community/netinfo';
import { BlurView } from 'expo-blur';
import { KeyboardProvider } from 'react-native-keyboard-controller';
import * as ScreenOrientation from 'expo-screen-orientation';
import { Dimensions, TextInput } from 'react-native';
import { PATCH_VERSION, CACHE_VERSION } from '@/config/version';
import installChunkReloadGuard from '@/utils/chunkReload';
import installDomMutationGuard from '@/utils/domGuard';
import { enableFreeze } from 'react-native-screens';


// Set the maximum font size multiplier for all Text components
Text.defaultProps = Text.defaultProps || {};
Text.defaultProps.maxFontSizeMultiplier = 1.6; // Example value: caps scaling at 120%

// Set the maximum font size multiplier for all TextInput components
TextInput.defaultProps = TextInput.defaultProps || {};
TextInput.defaultProps.maxFontSizeMultiplier = 1.6; // Example value

// Full app version for Sentry tagging: <nativeVersion>.<otaPatch>
const FULL_APP_VERSION = `${Application.nativeApplicationVersion || '0'}.${PATCH_VERSION}`;

const ignoreErrorSubstrings = [
    'Object not found',
    'No error message',
    'Calling the \'getValueWithKeyAsync\' function has failed',
    'JWT expired',
    'NetworkError',
    'Network request failed',
    'network error',
    'Network request timed out',
    // Request-queue timeout (requestQueue.js, 60s cap) — transient, query system retries.
    'Request timed out',
    // Request-queue backpressure rejection — transient, query system retries.
    'Request queue is full',
    'App hanging for at least',
    'Namespace "goog.debug.Error" already declared.',
    'Unknown error: The Internet connection appears to be offline.',
    'Load Failed',
    'Unable to open URL',
    'Lock was stolen by another request',
    'Lock broken by another request',
    'Failed to Fetch',
    'Abort due to cancellation',
    'Permission denied:',
    'TLS error caused the secure connection to fail',
    'Failed to load all assets',
    'The operation is insecure',
    'The network connection was lost',
    'upstream request timeout',
    'upstream connect error',
    'The Internet connection appears to be offline.',
    '\'queue\' is not a function',
    'Notifications are not allowed for this application',
    // Meta in-app browsers (Instagram/Facebook) inject telemetry scripts that
    // throw on window.webkit.messageHandlers being undefined in their own
    // broken context. Not our code — pure noise on public form URLs.
    'window.webkit.messageHandlers',
    // Device storage full (AsyncStorage writes). Environmental — see CHURCH-APP-18Z.
    'No space left on device',
    'out of space',
    // Android OS content-capture OOMs while backgrounded — no app frames,
    // nothing actionable. See CHURCH-APP-1CQ.
    'OutOfMemoryError',
    'MainContentCaptureSession'
]

SplashScreen.preventAutoHideAsync();
SplashScreen.setOptions({
    duration: 1000,
    fade: true,
});

configureReanimatedLogger({
    level: ReanimatedLogLevel.warn,
    strict: false
});

installChunkReloadGuard();
installDomMutationGuard();
// Lets navigators freeze off-screen scenes via freezeOnBlur — buried screens
// keep state/effects but stop re-rendering on query/store updates until refocus.
enableFreeze(true);


const navigationIntegration = Sentry.reactNavigationIntegration({
    enableTimeToInitialDisplay: true,
});


LogBox.ignoreLogs([
    'Key "cancelled" in the image picker result is deprecated and will be removed in SDK 48, use "canceled" instead',
    "Constants.platform.ios.model has been deprecated in favor of expo-device's Device.modelName property. This API will be removed in SDK 45.",
    "Sending `onAnimatedValueUpdate` with no listeners registered."
]);


const setSentryUser = (user) => {
    Sentry.setUser({
        id: user?.id,
        username: `${user?.user_metadata?.firstName || ''} ${user?.user_metadata?.lastName || ''}`.trim(),
        email: user?.email || user?.user_metadata?.email,
        organization_id: user?.app_metadata?.organizationID
    });
}

// Bounds an await on the auth-startup path. The splash only hides after
// authComplete, so a hung network call here would hold it forever — every
// startup await gets a hard cap and callers fail open on timeout.
const withStartupTimeout = (promise, ms) =>
    Promise.race([
        promise,
        new Promise((_, reject) => setTimeout(() => reject(new Error(`Startup await timed out after ${ms}ms`)), ms)),
    ]);

const waitForActiveAppState = () => {
    if (Platform.OS === 'web' || AppState.currentState === 'active') return Promise.resolve();

    return new Promise((resolve) => {
        const subscription = AppState.addEventListener('change', (nextAppState) => {
            if (nextAppState !== 'active') return;
            clearTimeout(timeout);
            subscription.remove();
            resolve();
        });

        // 10s cap: if AppState never reports 'active' (cold-start race), resolve
        // anyway — FaceID can still prompt and the splash must not hang on this.
        const timeout = setTimeout(() => {
            subscription.remove();
            resolve();
        }, 10000);
    });
};

//Single writer for the OS app badge (native only): mirrors totalUnread from the
//loggedInUser cache. Must render INSIDE QueryPersistenceProvider.
const NotificationBadgeSync = () => {
    const { totalUnread } = useNotificationBadges();

    useEffect(() => {
        if (Platform.OS === 'web') return;
        Notifications.setBadgeCountAsync(totalUnread).catch(() => {});
    }, [totalUnread]);

    return null;
};

const App = () => {
    const pathname = usePathname();
    const segments = useSegments();
    const isPublicRoute = segments[0] === '(public)' || pathname === '/form-payment' || ['/f/', '/give/', '/event-stats/'].some((prefix) => pathname?.startsWith(prefix));
    const [authComplete, setAuthComplete] = useState(isPublicRoute);
    const [isAppLocked, setIsAppLocked] = useState(false);
    const [isUnlocking, setIsUnlocking] = useState(false);
    const [isSigningOutFromLockScreen, setIsSigningOutFromLockScreen] = useState(false);
    const [isConnected, setIsConnected] = useState(true);
    // null | 'offline' | 'retrying' — set while startup session restore is
    // waiting on a bad network; drives the "still trying" splash message.
    const [startupAuthRetry, setStartupAuthRetry] = useState(null);
    const [isStartupSigningOut, setIsStartupSigningOut] = useState(false);
    const [persistBuster, setPersistBuster] = useState('');
    const [persistenceSession, setPersistenceSession] = useState(0);
    const [cacheHydrated, setCacheHydrated] = useState(false);

    const {isWeb, isNative} = usePlatform();
    const { width: windowWidth, height: windowHeight } = useWindowDimensions();
    const { checksComplete, isInMaintenance, maintenanceMessage, updateRequired } = useStartupChecks();
    const [deviceColorScheme, setDeviceColorScheme] = useState(Appearance.getColorScheme());
    // Per-field selectors — a whole-store subscription re-renders the root
    // layout (and the entire app tree below it) on every store write.
    const authStoreTheme = useAuthStore(s => s.theme);
    const authStoreIsEmulating = useAuthStore(s => s.isEmulating);
    const authStoreEmulatedUserName = useAuthStore(s => s.emulatedUserName);
    const setAuthStoreTheme = useAuthStore(s => s.setTheme);
    const setAuthStoreEmulation = useAuthStore(s => s.setEmulation);
    const setLastSentryEventID = useAuthStore(s => s.setLastSentryEventID);
    const kioskModeEnabled = useKioskStore(s => s.kioskModeEnabled);
    const kioskEventInstanceID = useKioskStore(s => s.eventInstanceID);
    const hydrateKiosk = useKioskStore(s => s.hydrate);
    const kioskHydrated = useKioskStore(s => s._hasHydrated);
    const router = useRouter();
    let [fontsLoaded] = useFonts({
        Geist_light: require('@/assets/fonts/Geist/static/Geist-Light.ttf'),
        Geist_regular: require('@/assets/fonts/Geist/static/Geist-Regular.ttf'),
        Geist_medium: require('@/assets/fonts/Geist/static/Geist-Medium.ttf'),
        Geist_semibold: require('@/assets/fonts/Geist/static/Geist-SemiBold.ttf'),
        Geist_bold: require('@/assets/fonts/Geist/static/Geist-Bold.ttf'),
    });
    const scheme = (authStoreTheme == 'light' || authStoreTheme == 'dark') ? authStoreTheme : deviceColorScheme;
    const { updatesComplete, updateStatus } = useStartupUpdates(scheme);

    //Gate on cacheHydrated so a cold-start push tap (MESSAGE_RECEIVED deep link) only
    //redirects AFTER the persisted query cache (loggedInUser, etc.) is restored —
    //otherwise the conversation screen's permission guard pops the deep link before
    //loggedInUser resolves. Delaying is safe: getLastNotificationResponseAsync still
    //returns the cold-start tap when the effect finally runs. On web the observer
    //effect is a no-op regardless, so gating it here has no web impact.
    useNotificationObserver(!isPublicRoute && updatesComplete && checksComplete && authComplete && fontsLoaded && cacheHydrated);

    const loaderEnable = useLoader(s => s.enable);
    const loaderDisable = useLoader(s => s.disable);
    const isAdaptiveRef = useRef(false);
    const spontaneousSignOutCleanupInProgressRef = useRef(false);
    const authStartupStartedRef = useRef(false);
    const localAuthInProgressRef = useRef(false);
    const localAuthAttemptIDRef = useRef(0);
    const localAuthPromptMessageRef = useRef('Unlock Reach');
    const lockScreenSignOutInProgressRef = useRef(false);
    const gatePrev = useRef(null);

    // Memoized so <ThemeProvider> consumers only re-render when the scheme
    // actually changes — a fresh object each render would invalidate them all.
    const currentTheme = useMemo(() => ({
        ...(scheme == 'dark' ? darkTheme : lightTheme),
        scheme,
    }), [scheme]);

    useEffect(() => {
        if (!scheme) return;

        if (authStoreTheme !== 'default') {
            UnistylesRuntime.setAdaptiveThemes(false);
            isAdaptiveRef.current = false;
            const theme = {
                ...(scheme === 'dark' ? darkTheme : lightTheme),
                scheme,
            };
            UnistylesRuntime.updateTheme(scheme === 'dark' ? 'dark' : 'light', () => theme);
            UnistylesRuntime.setTheme(scheme === 'dark' ? 'dark' : 'light');
        } else {
            if (!isAdaptiveRef.current) {
                UnistylesRuntime.setAdaptiveThemes(true);
                isAdaptiveRef.current = true;
            }
        }
        StatusBar.setBarStyle(scheme === 'light' ? 'dark-content' : 'light-content', true);
        if (isNative && authStoreTheme !== 'default') {
            Appearance.setColorScheme(scheme);
        }
        if (isWeb) {
            document.documentElement.style.colorScheme = scheme;
        }
    }, [scheme, authStoreTheme, isNative, isWeb]);

    useEffect(() => {
        // Determines how push notifications are handled when the app is open and in use
        // Native only, Web will use realtime on the notifications table
        Notifications.setNotificationHandler({
            handleNotification: async (notification) => {
                return mobileNotificationHandler(notification, pathname, router);
            },
        });

        hydrateKiosk();
        usePinnedPostsStore.getState().hydrate();
        useBoardsFeedStore.getState().hydrate();

        const subscription = Appearance.addChangeListener(({ colorScheme }) => {
            setDeviceColorScheme(colorScheme);
        });

        return () => subscription?.remove();
    }, []);

    useEffect(() => {
        // Kiosk mode route guard - redirect to check-in screen if kiosk mode is active
        if (!isPublicRoute && kioskHydrated && kioskModeEnabled && kioskEventInstanceID && authComplete) {
            const kioskPath = `/events/instances/${kioskEventInstanceID}/checkin`;
            if (!pathname.includes(kioskPath)) {
                router.replace(kioskPath);
            }
        }
    }, [kioskModeEnabled, kioskEventInstanceID, pathname, authComplete, kioskHydrated, isPublicRoute]);

    useEffect(() => {
        // Automated browser runs (Playwright etc.) report under the "e2e"
        // environment so test traffic stays searchable but never pollutes
        // production alerting. navigator.webdriver is true in Playwright
        // Chromium; __PLAYWRIGHT_TEST__ is injected by test fixtures.
        const isE2ERun = Platform.OS === 'web'
            && typeof window !== 'undefined'
            && (window.navigator?.webdriver === true || window.__PLAYWRIGHT_TEST__ === true);

        if(!__DEV__ && (Platform.OS !== 'web' || !['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname))){
            Sentry.init({
                dsn: process.env.EXPO_PUBLIC_SENTRY_DSN,
                tracesSampleRate: 0.2,
                profilesSampleRate: 0.1,
                environment: isE2ERun ? "e2e" : "production",
                attachScreenshot: true,
                enableLogs: true,
                enableMetrics: true,
                enableAppHangTracking: false,
                enableTombstone: true,
                //Replays cause jitter in scrolling
                // replaysSessionSampleRate: 0.1,
                // replaysOnErrorSampleRate: 1.0,
                replaysSessionQuality: 'low',
                beforeSendTransaction: (event) => {
                    if (isE2ERun) event.tags = { ...(event.tags || {}), e2e: 'true' };
                    const name = event?.transaction || event?.message || '';
                    if (typeof name !== 'string') return event;
                    const shouldIgnore = ignoreErrorSubstrings.some(substring => name.toLowerCase().includes(substring.toLowerCase()));
                    if (shouldIgnore) return null;
                    return event;
                },
                beforeSend: (event, hint) => {
                    // Drop e2e/test traffic — seeded sessions and test accounts
                    // use `e2e-` user ids and `@example.com` emails
                    if (event.user?.id?.startsWith('e2e-') || event.user?.email?.endsWith('@example.com')) {
                        return null;
                    }

                    //Drop errors
                    const error = hint?.originalException;

                    // Filter out native app hangs (mechanism: AppHang)
                    if (event?.tags?.mechanism === 'AppHang') {
                        return null;
                    }

                    // Extract message from multiple possible locations
                    const allValues = event?.exception?.values?.map(v => `${v?.type || ''} ${v?.value || ''}`).join(' ') || '';
                    const message =
                        error?.message ||
                        event?.message ||
                        allValues ||
                        event?.breadcrumbs?.[0]?.message ||
                        '';

                    const shouldIgnore = ignoreErrorSubstrings.some(substring => message.toLowerCase().includes(substring.toLowerCase()));

                    if (shouldIgnore) {
                        return null;
                    }

                    setLastSentryEventID(event.event_id);

                    event.tags = {
                        ...(event.tags || {}),
                        fullAppVersion: FULL_APP_VERSION,
                        ...(isE2ERun ? { e2e: 'true' } : {}),
                    };

                    return event;
                },
                beforeSendLog: (log) => {
                    if (log.attributes?.['sentry.origin'] === 'auto.log.console' || log.level === "error") {
                        return null;
                    }

                    log.attributes = {
                        ...(log.attributes || {}),
                        fullAppVersion: FULL_APP_VERSION,
                        ...(isE2ERun ? { e2e: 'true' } : {}),
                    }

                    return log;
                },
                beforeSendMetric: (metric) => {
                    metric.attributes = {
                        ...(metric.attributes || {}),
                        fullAppVersion: FULL_APP_VERSION,
                        ...(isE2ERun ? { e2e: 'true' } : {}),
                    }

                    return metric;  
                },
                integrations: (integrations) => {
                    // Replays Causing jitter in scrolling 
                    // if (Platform.OS === "web") {
                    //     integrations.push(
                    //         Sentry.browserReplayIntegration({
                    //             maskAllText: false,
                    //             maskAllInputs: false,
                    //         }),
                    //     );
                    // }
                    
                    // integrations.push(
                    //     Sentry.mobileReplayIntegration({
                    //         maskAllText: false,
                    //         maskAllImages: false,
                    //         maskAllVectors: false,
                    //     }),
                    // );

                    if(navigationIntegration){
                        integrations.push(navigationIntegration);
                    }

                    return integrations;
                },
            });
            reportPendingSignOutContext().catch(() => {});
        }
    }, [])

    const onLayoutRootView = useCallback(() => {
        SplashScreen.hide()
    }, []);


    useEffect(() => {
        const unsubscribe = NetInfo.addEventListener(state => {
            setIsConnected(state.isConnected ?? false);
        });

        return () => unsubscribe();
    }, []);

    const backgroundedAtRef = useRef(null);
    const lastBackgroundDurationRef = useRef(null);
    const lastResumedAtRef = useRef(null);
    const currentAppStateRef = useRef(AppState.currentState);
    const appStateTransitionIDRef = useRef(0);
    const authAutoRefreshTransitionRef = useRef(Promise.resolve());

    const authenticateLocally = useCallback(async (promptMessage = localAuthPromptMessageRef.current) => {
        if (!isNative || isPublicRoute) {
            setIsAppLocked(false);
            return true;
        }

        if (localAuthInProgressRef.current || lockScreenSignOutInProgressRef.current) return false;

        localAuthInProgressRef.current = true;
        const attemptID = ++localAuthAttemptIDRef.current;
        localAuthPromptMessageRef.current = promptMessage;
        setIsUnlocking(true);

        try {
            await waitForActiveAppState();
            if (attemptID !== localAuthAttemptIDRef.current) return false;

            const result = await LocalAuthentication.authenticateAsync({
                promptMessage,
                fallbackLabel: 'Use PIN',
                disableDeviceFallback: false,
            });

            if (attemptID !== localAuthAttemptIDRef.current) return false;

            if (result.success) {
                setIsAppLocked(false);
                return true;
            }

            Sentry.logger.warn('Local device authentication unsuccessful', {
                error: result.error,
                platform: Platform.OS,
                appState: currentAppStateRef.current || AppState.currentState,
            });
            setIsAppLocked(true);
            return false;
        } catch (error) {
            Sentry.logger.warn('Local device authentication threw an error', {
                error,
                platform: Platform.OS,
                appState: currentAppStateRef.current || AppState.currentState,
            });
            if (attemptID === localAuthAttemptIDRef.current) setIsAppLocked(true);
            return false;
        } finally {
            localAuthInProgressRef.current = false;
            setIsUnlocking(false);
        }
    }, [isNative, isPublicRoute]);

    useEffect(() => {
        if (!isNative || isPublicRoute) return;

        let cancelled = false;
        const updateAuthAutoRefresh = (nextAppState) => {
            const action = nextAppState === 'active' ? 'startAutoRefresh' : 'stopAutoRefresh';
            authAutoRefreshTransitionRef.current = authAutoRefreshTransitionRef.current
                .catch(() => {})
                .then(() => nextAppState === 'active'
                    ? supabase.auth.startAutoRefresh()
                    : supabase.auth.stopAutoRefresh())
                .catch((error) => {
                    Sentry.logger.warn('Failed to update auth auto-refresh for app state', {action, nextAppState, error});
                });
            return authAutoRefreshTransitionRef.current;
        };

        const handleAppStateChange = (nextAppState) => {
            const transitionID = ++appStateTransitionIDRef.current;
            currentAppStateRef.current = nextAppState;

            if (nextAppState === 'background' || nextAppState === 'inactive') {
                if (!backgroundedAtRef.current) backgroundedAtRef.current = Date.now();
                updateAuthAutoRefresh(nextAppState);
                return;
            }

            if (nextAppState !== 'active') return;

            const backgroundedAt = backgroundedAtRef.current;
            backgroundedAtRef.current = null;
            updateAuthAutoRefresh(nextAppState).then(() => {
                if (cancelled || transitionID !== appStateTransitionIDRef.current || currentAppStateRef.current !== 'active' || !backgroundedAt) return;

                const elapsed = Date.now() - backgroundedAt;
                lastBackgroundDurationRef.current = elapsed;
                lastResumedAtRef.current = Date.now();

                if (elapsed > 10 * 60 * 1000) {
                    const runResumeChecks = async () => {
                        try {
                            loaderEnable();
                            if(!__DEV__) await checkForAppUpdates();
                            await supabase.rpc('refresh_user_permissions');
                            // Preserve forced refresh after long resumes when the current
                            // JWT has more than five minutes left. Restarted auto-refresh
                            // handles sessions that are already close to expiry.
                            const session = (await supabase.auth.getSession()).data?.session;
                            const secondsLeft = session?.expires_at ? session.expires_at - Date.now() / 1000 : 0;
                            if (secondsLeft > 300) {
                                await ensureFreshSession();
                            }
                        } catch (error) {
                            Sentry.logger.warn('Backgrounding refresh failed', { error });
                        } finally {
                            loaderDisable();
                        }
                    };

                    runResumeChecks().catch((error) => {
                        Sentry.logger.warn('Backgrounding refresh cleanup failed', {error});
                    });
                }
            });
        };

        const subscription = AppState.addEventListener('change', handleAppStateChange);
        handleAppStateChange(AppState.currentState);

        return () => {
            cancelled = true;
            subscription.remove();
            updateAuthAutoRefresh('inactive');
        };
    }, [isNative, isPublicRoute]);

    // Web-only: refresh the session when the tab becomes visible again.
    // Browsers throttle setTimeout/setInterval in background tabs, so
    // autoRefreshToken may not fire while the tab is hidden. When the user
    // returns, the access token may be expired → first query gets an RLS miss
    // → can trigger a spurious signOut. This listener proactively refreshes
    // the session BEFORE any queries fire, mirroring what the native AppState
    // handler does on resume.
    useEffect(() => {
        if (!isWeb || isPublicRoute) return;

        const handleVisibilityChange = async () => {
            if (document.visibilityState !== 'visible') return;

            const { data: { session } } = await supabase.auth.getSession();
            if (!session) return;

            const secondsLeft = session.expires_at ? session.expires_at - Date.now() / 1000 : 0;
            if (secondsLeft < 300) {
                try {
                    await ensureFreshSession();
                } catch (error) {
                    Sentry.logger.warn('Web visibility session refresh failed', { error });
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [isWeb, isPublicRoute]);

    // Handle orientation based on device type (phone vs tablet)
    useEffect(() => {
        if (isWeb) return;

        const setupOrientation = async () => {
            try {
                const { width, height } = Dimensions.get('window');
                const aspectRatio = Math.max(width, height) / Math.min(width, height);
                
                // Consider device a tablet if aspect ratio is close to square or screen is large enough
                // Tablets typically have aspect ratios closer to 4:3 or 16:10, and larger screens
                const isTablet = aspectRatio < 1.6 || Math.min(width, height) >= 768;
                
                if (isTablet) {
                    // Allow all orientations on tablets
                    await ScreenOrientation.unlockAsync();
                } else {
                    // Lock to portrait on phones
                    await ScreenOrientation.unlockAsync();
                    await ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.PORTRAIT_UP);
                }
            } catch (error) {
                console.warn('Error setting orientation:', error);
            }
        };

        setupOrientation();

        // Listen for dimension changes (e.g., device rotation, split screen)
        const subscription = Dimensions.addEventListener('change', setupOrientation);
        
        return () => {
            subscription?.remove();
        };
    }, [isWeb]);


    useEffect(() => {
        // Never prompt on the old bundle before a startup OTA reload.
        if (!updatesComplete) return;

        if (isPublicRoute) {
            localAuthAttemptIDRef.current += 1;
            setIsAppLocked(false);
            setAuthComplete(true);
            return;
        }

        // authComplete is a one-way latch: it only starts false on a non-public
        // cold start. Do NOT set it back to false here — the loading gate below
        // renders INSTEAD of <Slot/>, so re-gating during a public → app
        // transition unmounts the root navigator mid-navigation, drops the
        // pending route change, and the remounted navigator re-reads the
        // initial deep link (the public-form back-button reload loop). Auth
        // startup instead runs once, in the background, on first app entry.
        if (authStartupStartedRef.current) return;
        authStartupStartedRef.current = true;

        let startupCancelled = false;

        const finishAuthStartup = () => {
            setAuthComplete(true);
        };

        // Resilient session restore: a transient network failure during
        // getSession() (expired access token → refresh fetch fails/hangs)
        // resolves { session: null, error } WITHOUT destroying the persisted
        // session — reading only data.session here would kick a valid user to
        // login on a slow network. restoreSessionWithRetry retries transient
        // errors with backoff (gated on NetInfo) and returns null ONLY for a
        // genuinely absent/invalid session.
        restoreSessionWithRetry({
            onRetry: (_attempt, reason) => {
                if (!startupCancelled) setStartupAuthRetry(reason);
            },
            // A user-initiated sign out from the "still trying" screen aborts
            // the loop promptly instead of waiting out the backoff.
            shouldAbort: () => startupCancelled || isSignOutInProgress(),
        }).then(async (session) => {
            if (!startupCancelled) setStartupAuthRetry(null);

            if(!session || !session?.user){
                // null here means genuinely absent/invalid session — or an
                // aborted retry (user signed out). Only log the real case.
                if (!startupCancelled && !isSignOutInProgress()) {
                    Sentry.logger.warn('No auth session restored on startup', {
                        platform: Platform.OS,
                        appState: currentAppStateRef.current || AppState.currentState,
                    });
                }
                setAuthComplete(true);
                return false;
            }

            setSentryUser(session.user);
            await waitForActiveAppState();

            const authOrganizationID = session?.user?.app_metadata?.organizationID;

            // Fold the plan identity into the persist buster so a plan
            // transition (org upgrade/downgrade) while the app was closed
            // discards the entire persisted query blob — stale paid-module
            // data can never render under a Free plan. A transient RPC
            // failure falls back to the last persisted plan marker so a blip
            // doesn't churn the buster and drop the cache unnecessarily.
            let planSuffix = 'boot';
            if (authOrganizationID) {
                try {
                    const bootEntitlements = await withStartupTimeout(getOrganizationEntitlements(), 4000);
                    if (bootEntitlements?.member === true) {
                        planSuffix = `${bootEntitlements.softwarePlan || 'unknown'}:${bootEntitlements.planRevision ?? 'unknown'}`;
                        // Prime the entitlements query so the (tabs) cold-start
                        // gate and layouts reuse this response.
                        queryClient.setQueryData(
                            QueryKeys.organizationEntitlements.current(getCachedOrgID() || authOrganizationID),
                            bootEntitlements
                        );
                    }
                } catch {
                    try {
                        const marker = await readPlanMarker();
                        if (marker?.organizationID === authOrganizationID) {
                            planSuffix = `${marker.softwarePlan || 'unknown'}:${marker.planRevision ?? 'unknown'}`;
                        }
                    } catch { /* keep 'boot' */ }
                }
            }

            setPersistBuster(`${session.user.id}:${authOrganizationID || ''}:${planSuffix}:${PATCH_VERSION}:${CACHE_VERSION}`);

            if(authOrganizationID && session?.user){
                // 8s cap: fetchQuery has no timeout, so a hung network would
                // stall the splash. On timeout treat as inconclusive and
                // CONTINUE startup — fail open, never sign out on a slow network.
                let userStillInOrg = null;
                try {
                    userStillInOrg = await withStartupTimeout(isUserStillInOrg(session?.user?.id, authOrganizationID), 8000);
                } catch {
                    userStillInOrg = null;
                }

                if(userStillInOrg === false){
                    await logOutOfOrganization()
                    setAuthComplete(true);
                    return false;
                }
            }

            const compatible = await LocalAuthentication.hasHardwareAsync();
            const authenticatedUser = session?.user;

            if(!authenticatedUser){
                Sentry.logger.warn('No authenticated user found in Auth Session', {session});

                await signOut('local', 'startup-session-user-missing');
                setAuthComplete(true);
                return false;
            }

            // Device guard (native only): if this device's row is gone from the
            // Devices table, the behavior depends on WHY it's gone:
            //
            // - No DEVICE_ID stored (null/undefined/"null"/"undefined"): the
            //   device was never registered (e.g. registration failed on login).
            //   Benign — fail open, continue without push notifications. Do NOT
            //   sign out, or users with flaky push registration get locked out.
            //
            // - Valid DEVICE_ID but row not found: the row was deleted, which
            //   now (after createDevice's token-collision cleanup was removed)
            //   only happens when the user explicitly removed this device from
            //   another device (settings/devices.jsx). This is a security
            //   action — sign out so a shared/lost device can't access the
            //   account. E.g. user logs into a shared iPad, forgets to sign
            //   out, removes the device from their phone → iPad gets logged out
            //   on next open.
            //
            // - Lookup error (network/etc.): fail open — transient errors must
            //   not log the user out.
            //
            // Web is excluded entirely (no device registration).
            if(isNative){
                try {
                    const deviceID = await AsyncStorage.getItem('DEVICE_ID');

                    if(!deviceID || deviceID === 'null' || deviceID === 'undefined'){
                        // Never registered — benign, continue without push
                        Sentry.logger.warn('No valid DEVICE_ID stored on app open — continuing without device registration');
                    } else {
                        // 8s cap: a hung lookup must not stall the splash —
                        // the catch below fails open on timeout like any
                        // other lookup error
                        const device = await withStartupTimeout(getDeviceByID(deviceID), 8000);
                        if(!device){
                            // Row was deleted (user removed device from another
                            // device) — security sign-out
                            Sentry.logger.warn('Stored DEVICE_ID not found in Devices table — signing out (device likely removed by user)', {deviceID});
                            await signOut('local', 'startup-device-removed');
                            setAuthComplete(true);
                            return false;
                        }
                    }
                } catch (error) {
                    // Fail open: a transient lookup/network error must not log the user out
                    Sentry.logger.warn('Device registration check failed', {error});
                }
            }

            if(isWeb){
                setNotificationRealtimeUpdates(authenticatedUser)
            }

            const biometricEnabled = await AsyncStorage.getItem("@enableBiometrics");

            if(__DEV__ || isWeb){
                setIsAppLocked(false);
                setAuthComplete(true);
                setSentryUser(authenticatedUser);

                return true;
            }

            const promptMessage = compatible && biometricEnabled && biometricEnabled !== "false"
                ? 'Unlock Reach'
                : 'Enter your device PIN';
            const unlocked = await authenticateLocally(promptMessage);

            if(unlocked){
                setSentryUser(authenticatedUser);
            }

            return unlocked;
          
        })
        .catch(async (error) => {
            Sentry.captureException(error, {
                tags: {authStartupFailure: true},
                extra: {
                    platform: Platform.OS,
                    appState: currentAppStateRef.current || AppState.currentState,
                },
            });
            Sentry.logger.warn('Auth startup operation failed', {
                platform: Platform.OS,
                appState: currentAppStateRef.current || AppState.currentState,
                error,
            });

            if (isNative) {
                await authenticateLocally();
            } else {
                setIsAppLocked(false);
            }
            return false;
        })
        .finally(finishAuthStartup);

        return () => {
            startupCancelled = true;
        };

    }, [authenticateLocally, isNative, isPublicRoute, updatesComplete]);

    // React to auth state loss without issuing another auth sign-out request.
    // SIGNED_OUT means Supabase already removed the session; only local state remains.
    useEffect(() => {
        if (isPublicRoute) return;

        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
            // A signed-out (or newly signed-in) session invalidates the startup
            // checks — device registration, persist buster, local lock. Allow
            // them to re-run next time the app is entered from a public route.
            // Re-running is now safe: startup no longer drops authComplete, so
            // it can't unmount <Slot/> mid-navigation.
            if (event === 'SIGNED_OUT' || event === 'SIGNED_IN') {
                authStartupStartedRef.current = false;
            }
            if (session?.user && ['INITIAL_SESSION', 'SIGNED_IN', 'TOKEN_REFRESHED', 'USER_UPDATED'].includes(event)) {
                const prefix = `${session.user.id}:${session.user.app_metadata?.organizationID || ''}:`;
                setPersistBuster(current => current.startsWith(prefix)
                    ? current
                    : `${prefix}boot:${PATCH_VERSION}:${CACHE_VERSION}`);
                // A same-account login must resume persistence after logout
                // invalidated its pending writes, even if the buster is equal.
                if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN') setPersistenceSession(current => current + 1);
            }

            if (event === 'SIGNED_OUT') {
                // auth-js only emits SIGNED_OUT when it actually removed the
                // session: explicit signOut, invalid stored session, or a
                // NON-retryable refresh failure (invalid_grant etc.) with an
                // expired access token. Transient refresh failures (network
                // error, 5xx, and — via throttledFetch normalization — captive
                // portals/429s) never reach this handler: the session is left
                // in storage and getSession() just returns { session: null,
                // error }, which the startup restore loop retries instead.
                localAuthAttemptIDRef.current += 1;
                setIsAppLocked(false);

                if (isSignOutInProgress() || spontaneousSignOutCleanupInProgressRef.current) return;

                spontaneousSignOutCleanupInProgressRef.current = true;
                const now = Date.now();
                const appState = currentAppStateRef.current || AppState.currentState;
                const backgroundDurationMs = backgroundedAtRef.current
                    ? now - backgroundedAtRef.current
                    : lastBackgroundDurationRef.current;
                const authLossContext = {
                    event,
                    platform: Platform.OS,
                    appState,
                    ...(backgroundDurationMs !== null ? {backgroundDurationMs} : {}),
                    ...(lastResumedAtRef.current ? {millisecondsSinceResume: now - lastResumedAtRef.current} : {}),
                };

                Sentry.logger.warn('Auth session lost without explicit sign out', authLossContext);

                try {
                    await flushSentryWithTimeout();
                    await cleanupLocalAuthState();
                } catch (error) {
                    Sentry.logger.warn('Local cleanup failed after spontaneous auth state loss', {error});
                } finally {
                    setAuthComplete(true);
                    spontaneousSignOutCleanupInProgressRef.current = false;
                }
                return;
            }

            if (event === 'TOKEN_REFRESHED' && session?.user) {
                setSentryUser(session.user);
            }
        });

        return () => subscription.unsubscribe();
    }, [isPublicRoute]);

    // Restore user's saved theme preference after auth completes (store isn't persisted)
    useEffect(() => {
        if (!authComplete || isPublicRoute) return;
        if (authStoreTheme === 'light' || authStoreTheme === 'dark') return;

        (async () => {
            try {
                const loggedInUser = await queryClient.fetchQuery({
                    queryKey: QueryKeys.people.loggedInUser(),
                    queryFn: getLoggedInUserInfo
                });
                const themeSetting = loggedInUser?.userSettings?.theme;
                if (themeSetting && themeSetting !== 'default') {
                    setAuthStoreTheme(themeSetting);
                }
            } catch {
                // Non-critical — fall back to device theme
            }
        })();
    }, [authComplete, isPublicRoute]);

    //I hate it, but need the newest pathname for desktop notifications
    const pathRef = useRef(pathname);
    useEffect(() => {
        pathRef.current = pathname;
    }, [pathname]);

    // Web-only: native uses Notifications.setNotificationHandler + mobileNotificationHandler (push notifications)
    // instead of realtime channels to avoid exhausting Supabase connection pool (Sentry CHURCH-APP-140).
    const setNotificationRealtimeUpdates = async (loggedInUser) => {
        // This is only for web
        // Mobile notifications are handled natively, but expo doesn't support desktop right now
        await supabase.realtime.setAuth();
        const channelTopic = `Notifications:${loggedInUser?.id}`;
        const channels = supabase.getChannels();

        // Drop Notifications channels for any other user — their broadcast
        // handlers close over the previous loggedInUser and would keep
        // surfacing that account's desktop notifications after a user switch.
        const staleChannels = channels.filter(c => c?.subTopic?.startsWith('Notifications:') && c.subTopic !== channelTopic);
        await Promise.all(staleChannels.map(c => supabase.removeChannel(c)));

        const existingChannel = channels.find(c => c?.subTopic === channelTopic);
        if (existingChannel) {
            // Auth startup re-runs on every SIGNED_IN — if this user's channel
            // is already subscribed, adding .on bindings again would stack
            // handlers (duplicate desktop notifications on each login).
            if (existingChannel.state === 'joined' || existingChannel.state === 'joining') return;
            // Channel exists but never successfully subscribed (errored/closed)
            // — remove it so the fresh channel below binds exactly once.
            await supabase.removeChannel(existingChannel);
        }

        supabase.channel(channelTopic, {
            config: { private: true },
        })
        .on('broadcast', { event: 'INSERT' }, (broadcastMessage) => {
            if(broadcastMessage?.payload?.record?.targetPersonID == loggedInUser?.id){
                desktopNotificationHandler(broadcastMessage?.payload?.record, pathRef.current, router, true, true)
            }
        })
        .on('broadcast', { event: 'UPDATE' }, (broadcastMessage) => {
            const messageChanged = broadcastMessage?.payload?.record?.message != broadcastMessage?.payload?.old_record?.message;
            const pathChanged = broadcastMessage?.payload?.record?.path != broadcastMessage?.payload?.old_record?.path;
            if(broadcastMessage?.payload?.record?.targetPersonID == loggedInUser?.id){
                desktopNotificationHandler(broadcastMessage?.payload?.record, pathRef.current, router, messageChanged, pathChanged)
            }
        })
        .subscribe()
    }

    const isUserStillInOrg = async (userID, orgID) => {
        const usersOrganizations = await queryClient.fetchQuery({queryKey: ["organizations", {userID: userID}], queryFn: () => getUsersOrganizations(userID)})
        if (!usersOrganizations) return null;
        return usersOrganizations.some(org => org.organizationID == orgID);
    }

    const handleUnlockPress = useCallback(() => {
        authenticateLocally();
    }, [authenticateLocally]);

    // Manual escape from the "connection slow — still trying" splash state.
    // Local-scope sign out: works offline, aborts the restore retry loop via
    // isSignOutInProgress, and the SIGNED_OUT event drops the user to login.
    const handleStartupSignOut = useCallback(async () => {
        if (isStartupSigningOut || isSignOutInProgress()) return;
        setIsStartupSigningOut(true);
        try {
            await signOut('local', 'startup-slow-network-user-requested');
        } catch (error) {
            Sentry.logger.warn('Startup retry screen sign out failed', { error });
        } finally {
            setIsStartupSigningOut(false);
        }
    }, [isStartupSigningOut]);

    const handleLockScreenSignOut = useCallback(async () => {
        if (localAuthInProgressRef.current || lockScreenSignOutInProgressRef.current) return;

        lockScreenSignOutInProgressRef.current = true;
        localAuthAttemptIDRef.current += 1;
        setIsSigningOutFromLockScreen(true);

        try {
            await signOut('local', 'unlock-screen-user-requested');
            setIsAppLocked(false);
            router.replace('/auth/login');
        } catch (error) {
            Sentry.logger.warn('Unlock screen sign out failed', {
                error,
                platform: Platform.OS,
                appState: currentAppStateRef.current || AppState.currentState,
            });
            setIsAppLocked(true);
        } finally {
            lockScreenSignOutInProgressRef.current = false;
            setIsSigningOutFromLockScreen(false);
        }
    }, [router]);

    const showingSideNav = ((isWeb ? windowWidth > windowHeight : UnistylesRuntime.isLandscape) && !kioskModeEnabled && !isPublicRoute && segments[0] !== "+not-found" && segments[0] !== "auth" && segments[0] !== "signup")

    // TEMP DEBUG: whichever root gate flips swaps the whole tree (startup
    // screen / lock screen / app) — that unmount+remount is the churn source.
    if (__DEV__) {
        const gates = { updatesComplete, checksComplete, authComplete, fontsLoaded, isAppLocked, isSigningOutFromLockScreen, isConnected };
        if (!gatePrev.current) gatePrev.current = gates;
        for (const k in gates) {
            if (gatePrev.current[k] !== gates[k]) console.log(`[app-gate] ${k}: ${gatePrev.current[k]} -> ${gates[k]}`);
        }
        gatePrev.current = gates;
    }

    return (
        <Sentry.ErrorBoundary fallback={<ErrorScreen scheme={scheme} />}>
            {(!updatesComplete || !checksComplete || !authComplete || !fontsLoaded) ? (
                <View testID="startup-screen" style={[StyleSheet.absoluteFill, { height: isWeb ? "100vh" : "100%", width: isWeb ? "100vw" : "100%", justifyContent: "center", alignItems: "center", backgroundColor: scheme == 'dark' ? "#121212" : "#306fb3" }]}>
                    {isNative && (
                        <Image
                            source={require('@/assets/AppIcons/splash-icon-light.png')}
                            style={{ width: 200, height: 200 }}
                            resizeMode="contain"
                            accessibilityLabel="Reach"
                            onLoad={onLayoutRootView}
                        />
                    )}
                    <View style={isNative ? styles.startupStatusBox : undefined}>
                        <ActivityIndicator color={isNative || scheme == 'dark' ? "#fff" : "#000"} size="large" />
                        {!!updateStatus && (
                            <Text
                                testID="startup-update-status"
                                accessibilityLiveRegion="polite"
                                style={[styles.startupRetryText, { marginTop: 16 }, !fontsLoaded && { fontFamily: undefined }]}
                            >
                                {updateStatus}
                            </Text>
                        )}
                        {startupAuthRetry && (
                            <View style={styles.startupRetryBox}>
                                <Text style={styles.startupRetryText}>
                                    {startupAuthRetry === 'offline'
                                        ? 'No internet connection — waiting to reconnect…'
                                        : 'Your connection seems slow — still trying…'}
                                </Text>
                                <TouchableOpacity
                                    testID="startup-auth-sign-out"
                                    accessibilityRole="button"
                                    accessibilityLabel="Sign out"
                                    activeOpacity={0.7}
                                    disabled={isStartupSigningOut}
                                    onPress={handleStartupSignOut}
                                    style={styles.startupSignOutButton(isStartupSigningOut)}
                                >
                                    <Text style={styles.startupSignOutText}>
                                        {isStartupSigningOut ? 'Signing Out...' : 'Sign Out'}
                                    </Text>
                                </TouchableOpacity>
                            </View>
                        )}
                    </View>
                </View>
            ) : updateRequired ? (
                <UpdateRequiredScreen currentVersion={updateRequired.current} minimumVersion={updateRequired.minimum} />
            ) : isInMaintenance ? (
                <MaintenanceScreen message={maintenanceMessage} scheme={scheme} />
            ) : (!isPublicRoute && isNative && (isAppLocked || isSigningOutFromLockScreen)) ? (
                <View testID="local-auth-lock-screen" onLayout={onLayoutRootView} style={styles.lockScreen}>
                    <View style={styles.lockCard}>
                        <Text style={styles.lockTitle}>Reach is locked</Text>
                        <Text style={styles.lockCopy}>
                            Device authentication is required to unlock Reach and protect your account.
                        </Text>

                        <View style={styles.lockActions}>
                            <TouchableOpacity
                                testID="local-auth-unlock-button"
                                accessibilityRole="button"
                                accessibilityLabel="Unlock Reach"
                                activeOpacity={0.8}
                                disabled={isUnlocking || isSigningOutFromLockScreen}
                                onPress={handleUnlockPress}
                                style={styles.unlockButton(isUnlocking || isSigningOutFromLockScreen)}
                            >
                                {isUnlocking ? (
                                    <ActivityIndicator color={currentTheme.colors.onPrimary} size="small" />
                                ) : (
                                    <Text style={styles.unlockButtonText}>Unlock</Text>
                                )}
                            </TouchableOpacity>

                            <TouchableOpacity
                                testID="local-auth-sign-out-button"
                                accessibilityRole="button"
                                accessibilityLabel="Sign out"
                                activeOpacity={0.7}
                                disabled={isUnlocking || isSigningOutFromLockScreen}
                                onPress={handleLockScreenSignOut}
                                style={styles.lockSignOutButton(isUnlocking || isSigningOutFromLockScreen)}
                            >
                                <Text style={styles.lockSignOutButtonText}>
                                    {isSigningOutFromLockScreen ? 'Signing Out...' : 'Sign Out'}
                                </Text>
                            </TouchableOpacity>
                        </View>
                    </View>
                </View>
            ) : (
                <GestureHandlerRootView>
                    <QueryPersistenceProvider
                        client={queryClient}
                        sessionKey={persistenceSession}
                        onSuccess={() => setCacheHydrated(true)}
                        //If hydration fails, still unblock the notification observer —
                        //better to race the cache than to never handle push taps.
                        onError={() => setCacheHydrated(true)}
                        persistOptions={{
                            persister: asyncStoragePersister,
                            buster: persistBuster,
                            maxAge: 1000 * 60 * 60 * 24,
                            dehydrateOptions: {
                                shouldDehydrateQuery: shouldPersistQuery,
                            },
                        }}
                    >
                            <KeyboardProvider>
                                <GuideProvider blocked={isPublicRoute || segments[0] !== '(tabs)' || kioskModeEnabled || authStoreIsEmulating || !isConnected}>
                                <View onLayout={onLayoutRootView} style={styles.container}>
                                    {isWeb && <SmartAppBanner />}
                                    
                                    {authStoreIsEmulating &&
                                        <TouchableOpacity style={styles.emulationBanner} onPress={async () => {
                                            loaderEnable();
                                            setAuthStoreEmulation(false);
                                            queryClient.invalidateQueries({ queryKey: QueryKeys.people.loggedInUser() });
                                            queryClient.refetchQueries({ queryKey: QueryKeys.people.loggedInUser() }).then(() => {
                                                loaderDisable();
                                            })
                                        }}>
                                            <Text style={{ textAlign: "center" }}>Emulating <Text style={{ fontWeight: "bold" }}>{authStoreEmulatedUserName}</Text>. Press here to stop.</Text>
                                        </TouchableOpacity>
                                    }

                                    {isWeb && <ConfirmationModal />}

                                    {(showingSideNav) && <WebHeader />}

                                    <View style={styles.bodyRow(showingSideNav)}>
                                        {showingSideNav && <DrawerMenu />}

                                        <View style={styles.appWrapper(showingSideNav)}>
                                            <ThemeProvider value={currentTheme}>
                                                <Slot />
                                            </ThemeProvider>
                                        </View>
                                    </View>

                                    <FullScreenLoader />
                                    <TooltipContainer />
                                    {!isPublicRoute && <NotificationBadgeSync />}
                                    {!isPublicRoute && segments[0] === '(tabs)' && <>
                                        <AchievementHost />
                                        <MilestoneCelebrationProvider />
                                    </>}
                                </View>
                                </GuideProvider>
                            </KeyboardProvider>
                    </QueryPersistenceProvider>
                    <ReachToast />
                </GestureHandlerRootView>
            )}

            {!isConnected && (
                <BlurView intensity={50} tint={scheme == 'dark' ? "dark" : "light"} style={[StyleSheet.absoluteFill, { height: "100vh", width: "100vw", justifyContent: "center", alignItems: "center" }]}>
                    <Text style={styles.noInternetText}>No internet connection... Attempting to reconnect...</Text>
                </BlurView>
            )}
        </Sentry.ErrorBoundary>
    );
}

const styles = StyleSheet.create(({ colors, spacing, roundness, typography, fonts, shadows }, rt) => ({
    container: {
        backgroundColor: colors.background,
        flex: 1,
    },
    lockScreen: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        padding: spacing.lg,
        backgroundColor: colors.background,
    },
    lockCard: {
        width: '100%',
        maxWidth: 420,
        alignItems: 'center',
        padding: spacing.xl,
        borderRadius: roundness.xl,
        backgroundColor: colors.surface,
        boxShadow: shadows.elevated,
    },
    lockTitle: {
        color: colors.text,
        fontFamily: fonts.bold,
        fontSize: typography.h1,
        textAlign: 'center',
    },
    lockCopy: {
        maxWidth: 320,
        marginTop: spacing.sm,
        color: colors.textSecondary,
        fontFamily: fonts.body,
        fontSize: typography.body,
        lineHeight: 22,
        textAlign: 'center',
    },
    lockActions: {
        width: '100%',
        marginTop: spacing.xl,
        gap: spacing.sm,
    },
    unlockButton: (disabled) => ({
        minHeight: 48,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: spacing.md,
        paddingVertical: spacing.sm,
        borderRadius: roundness.md,
        backgroundColor: colors.primary,
        opacity: disabled ? 0.6 : 1,
    }),
    unlockButtonText: {
        color: colors.onPrimary,
        fontFamily: fonts.bold,
        fontSize: typography.body,
    },
    lockSignOutButton: (disabled) => ({
        minHeight: 44,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: spacing.md,
        paddingVertical: spacing.sm,
        borderRadius: roundness.md,
        opacity: disabled ? 0.5 : 1,
    }),
    lockSignOutButtonText: {
        color: colors.error,
        fontFamily: fonts.bold,
        fontSize: typography.body,
    },
    noInternetText: {
        textAlign: "center",
        color: colors.text,
        fontSize: 16,
    },
    // Splash retry state sits on the brand-blue/dark splash background (fixed
    // colors above, not theme surface) — white text reads on both.
    startupRetryBox: {
        marginTop: spacing.xl,
        alignItems: 'center',
        paddingHorizontal: spacing.lg,
    },
    startupStatusBox: {
        position: 'absolute',
        top: '50%',
        marginTop: 124,
        width: '100%',
        alignItems: 'center',
        paddingHorizontal: spacing.lg,
    },
    startupRetryText: {
        color: '#fff',
        fontFamily: fonts.body,
        fontSize: typography.body,
        textAlign: 'center',
    },
    startupSignOutButton: (disabled) => ({
        minHeight: 44,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: spacing.md,
        paddingVertical: spacing.sm,
        marginTop: spacing.md,
        opacity: disabled ? 0.5 : 1,
    }),
    startupSignOutText: {
        color: '#fff',
        fontFamily: fonts.bold,
        fontSize: typography.body,
        textDecorationLine: 'underline',
    },
    emulationBanner: {
        zIndex: 10000,
        width: rt.screen.width,
        height: 80,
        marginBottom: -50,
        paddingBottom: 5,
        backgroundColor: colors.success,
        alignItems: "center",
        justifyContent: "flex-end",
    },
    bodyRow: (showingSideNav) => ({
        flexDirection: showingSideNav ? "row" : "column",
        flex: 1,
        height: showingSideNav ? rt.screen.height - 64 : rt.screen.height,
    }),
    appWrapper: (showingSideNav) => ({
        // flex: 1 (not screen.width - sideNavWidth) so the content absorbs the
        // space freed while DrawerMenu animates its collapsed width.
        ...(showingSideNav ? { flex: 1, minWidth: 0 } : { width: rt.screen.width }),
        height: showingSideNav ? rt.screen.height - 64 : rt.screen.height,
        backgroundColor: colors.background,
    }),
}));

export default __DEV__ ? App : Sentry.wrap(App);